import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
// Replace incorrect hook import with the actual AuthContext hook
import { useAuth } from '@/components/auth/AuthContext';
import { RouterHeader } from './components/RouterHeader';
import { FooterEnhanced } from './components/FooterEnhanced';
import { RecruitmentPopup } from './components/RecruitmentPopup';
import Loader from '@/components/ui/Loader';

// Pages - importing from pages where they actually exist
import { HomePage as Home } from './pages/HomePage';
import { SignInPage } from './pages/SignInPage';
import { JobListings as Jobs } from './components/JobListings';
import { JobDetail as JobDetails } from './components/JobDetail';

import UserProfile from './pages/UserProfile';
import { JobSeekerDashboardLayout } from './components/jobseeker/JobSeekerDashboardLayout';
import { DashboardHome } from './components/jobseeker/DashboardHome';
import { JobsSection } from './components/jobseeker/JobsSection';
import { SavedJobsSection } from './components/jobseeker/SavedJobsSection';
import { ApplicationsSection } from './components/jobseeker/ApplicationsSection';
import { ResumeSection } from './components/jobseeker/ResumeSection';
import { AlertsSection } from './components/jobseeker/AlertsSection';
import { SettingsSection } from './components/jobseeker/SettingsSection';
import { EditProfileSection } from './components/jobseeker/EditProfileSection';
import { FollowingSection } from './components/jobseeker/FollowingSection';
import { EmployerLayout } from './components/EmployerLayout';
// import removed: legacy AdminDashboard alias not used
import { AboutPage } from './components/AboutPage';
import { ContactPage } from './components/ContactPage';
import { BlogsPage } from './pages/BlogsPage';
import { FaqsPage } from './components/FaqsPage';
import { EmployersPage } from './components/EmployersPageNew';
import { CompanyDetailPage } from './components/CompanyDetailPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';

// Additional components
import { JobMapView } from './components/JobMapView';
import { PerformanceMonitor } from './components/PerformanceMonitor';

import { EmergencyApp } from './components/EmergencyApp';
// Auth: forgot/reset password forms
import { ForgotPasswordForm } from './components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { ResetPasswordWithOtpForm } from './components/auth/ResetPasswordWithOtpForm';
import { slugify } from './utils/slug';

// Super Admin pages
import SuperAdminLoginPage from './pages/super-admin/login/page';
import SuperAdminDashboardPage from './pages/super-admin/page';

// Resume Builder components
import ResumeBuilder from './components/resume/ResumeBuilder';
import ResumeList from './components/resume/ResumeList';
import SharedResumeView from './components/resume/SharedResumeView';
import { useApp } from './pages/providers/AppProvider';
import { useData } from './context/DataContext';
import { ErrorBoundary } from './components/ErrorBoundary';

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | null;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole = null }) => {
  const { user, isAuthenticated, loading } = useAuth();

  const normalizeRole = (r?: string | null): string => {
    const v = String(r || '').toLowerCase();
    if (v === 'jobseeker') return 'job_seeker';
    if (v === 'superadmin') return 'super_admin';
    return v;
  };
  const userRole = normalizeRole(user?.user_type || null);
  const required = normalizeRole(requiredRole);

  const roleAllowed = (() => {
    if (!required) return true;
    if (userRole === required) return true;
    if (required === 'admin' && userRole === 'super_admin') return true;
    return false;
  })();

  if (loading) {
    return <Loader fullScreen text="Checking authentication..." />;
  }

  if (isAuthenticated && required && !user) {
    return <Loader fullScreen text="Loading your profile..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!roleAllowed) {
    if (required === 'super_admin') {
      return <Navigate to="/super-admin/login" replace />;
    }
    if (required === 'admin') {
      return <Navigate to="/super-admin/login" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect if already authenticated)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    const role = (() => {
      if (user && user.user_type) return String(user.user_type).toLowerCase();
      try {
        const token = localStorage.getItem('megajobnepal_auth_token');
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            return String(payload?.user_type || '').toLowerCase();
          }
        }
      } catch { }
      return '';
    })();
    if (role === 'job_seeker') return <Navigate to="/jobseeker-dashboard" replace />;
    if (role === 'employer') return <Navigate to="/employer/dashboard" replace />;
    if (role === 'admin') return <Navigate to="/super-admin/dashboard" replace />;
    if (role === 'super_admin') return <Navigate to="/super-admin/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Wrapper component for EmployersPage with navigation
const EmployersPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigation = (page: string, companyName?: string) => {
    switch (page) {
      case 'company-detail':
        if (companyName) {
          navigate(`/company/${encodeURIComponent(companyName)}`);
        }
        break;
      case 'jobs':
        navigate('/jobs');
        break;
      default:
        navigate(`/${page}`);
    }
  };

  return <EmployersPage onNavigate={handleNavigation} />;
};

// Wrapper component for All Companies page
const CompaniesPageWrapper: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigation = (page: string, companyName?: string) => {
    switch (page) {
      case 'company-detail':
        if (companyName) {
          navigate(`/company/${encodeURIComponent(companyName)}`);
        }
        break;
      case 'jobs':
        navigate('/jobs');
        break;
      default:
        navigate(`/${page}`);
    }
  };

  return <EmployersPage onNavigate={handleNavigation} showOnlyAll={true} />;
};

const AppRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { savedJobs, handleSaveJob, handleApplyJob, currentUser, applications } = useApp();
  const { jobs } = useData();

  const [showRecruitmentPopup, setShowRecruitmentPopup] = useState(false);

  // Force popup via query parameter override (e.g., ?showRecruitment=true or ?popup=recruitment)
  const params = new URLSearchParams(location.search);
  const forcePopup = params.get('showRecruitment') === 'true' || params.get('popup') === 'recruitment';

  // Use a ref to track the latest user state without triggering effect re-runs
  const userRef = React.useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    // If forced by query param, show immediately regardless of user type or prior dismissals
    if (forcePopup) {
      setShowRecruitmentPopup(true);
      return;
    }

    // Determine if current route is a dashboard/admin route (hide popup on these unless forced)
    const dashboardRoutes = [
      '/jobseeker-dashboard',
      '/jobseeker/dashboard',
      '/employer/dashboard',
      '/super-admin',
      '/super-admin/dashboard'
    ];
    const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));

    // Show popup after 2 seconds on every page load/refresh
    const timer = setTimeout(() => {
      // Don't show on dashboard routes
      if (isDashboardRoute) return;

      // Do not show for employer/admin users unless forced
      const currentUser = userRef.current;
      const userType = String(currentUser?.user_type || '');
      if (currentUser && (userType === 'employer' || userType === 'super_admin' || userType === 'admin')) {
        return;
      }

      setShowRecruitmentPopup(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [forcePopup]); // Removed location.pathname to only trigger on hard refresh/initial load

  const handleDismissPopup = () => {
    setShowRecruitmentPopup(false);
  };

  // Define dashboard routes that should not show the main header
  const dashboardRoutes = [
    '/jobseeker-dashboard',
    '/jobseeker/dashboard',
    '/employer/dashboard',
    // Hide header for all super-admin routes (login and dashboard)
    '/super-admin',
    '/super-admin/dashboard'
  ];

  const isDashboardRoute = dashboardRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className="min-h-screen flex flex-col">
      {!isDashboardRoute && <RouterHeader />}
      {((!isDashboardRoute) || forcePopup) && showRecruitmentPopup && (
        <RecruitmentPopup
          onClose={handleDismissPopup}
          onNavigate={(page: string) => navigate(`/${page}`)}
        />
      )}
      <main className="flex-1">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={
            <Home
              onNavigate={(page: string) => navigate(`/${page}`)}
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              filters={{
                location: '',
                type: '',
                category: '',
                salary: '',
                experience: ''
              }}
              onFilterChange={() => { }}
              onSaveJob={(id: string) => handleSaveJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
            />
          } />
          <Route path="/jobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
            />
          } />
          <Route path="/megajobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
              filter={{ type: 'tier', value: 'megajob' }}
            />
          } />
          <Route path="/premiumjobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
              filter={{ type: 'tier', value: 'premium' }}
            />
          } />
          <Route path="/primejobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
              filter={{ type: 'tier', value: 'prime' }}
            />
          } />
          <Route path="/latestjobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
              filter={{ type: 'tier', value: 'latest' }}
            />
          } />
          <Route path="/newspaperjobs" element={
            <Jobs
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onViewCompany={(name: string) => navigate(`/company/${encodeURIComponent(name)}`)}
              onSaveJob={(id: string) => handleSaveJob(id)}
              onApply={(id: string) => handleApplyJob(id)}
              savedJobs={savedJobs}
              isUserLoggedIn={isAuthenticated}
              onLoginRequired={() => navigate('/auth')}
              filter={{ type: 'tier', value: 'newspaper' }}
            />
          } />
          <Route path="/jobs/:id" element={<JobDetailsRoute />} />
          <Route path="/job/:id" element={<JobDetailsRoute />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/team/:name" element={<AboutPageRoute />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/contact/faqs" element={<FaqsPage />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/employers" element={<EmployersPageWrapper />} />
          <Route path="/companies" element={<CompaniesPageWrapper />} />
          <Route path="/company/:id" element={
            <CompanyDetailPage
              companyName=""
              jobs={[]}
              onViewJob={(id: string) => {
                const target = jobs.find(j => j.id === id);
                const slug = target ? slugify(String(target.title)) : id;
                navigate(`/jobs/${slug}`);
              }}
              onSaveJob={(id: string) => handleSaveJob(id)}
              savedJobs={savedJobs}
              onNavigate={(path: string) => navigate(path)}
            />
          } />
          <Route path="/performance" element={
            <PerformanceMonitor name="App Performance">
              <div>Performance monitoring content</div>
            </PerformanceMonitor>
          } />

          <Route path="/emergency" element={<EmergencyApp />} />

          {/* Public resume sharing route */}
          <Route path="/resume/shared/:token" element={<SharedResumeView />} />

          {/* Auth routes */}
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/register" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<PublicRoute><SignInPage /></PublicRoute>} />
          {/* Forgot/Reset Password routes */}
          <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordRoute />} />
          <Route path="/auth/reset-password-otp" element={<ResetPasswordOtpRoute />} />
          <Route path="/reset-password" element={<ResetPasswordRoute />} />
          <Route path="/auth/reset-password" element={<ResetPasswordRoute />} />
          <Route path="/admin/login" element={<Navigate to="/super-admin/login" replace />} />
          <Route path="/admin" element={<Navigate to="/super-admin/dashboard" replace />} />
          <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
          <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />

          {/* Protected routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          {/* Resume Builder routes */}
          <Route
            path="/resumes"
            element={
              <ProtectedRoute>
                <ResumeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume-builder"
            element={
              <ProtectedRoute>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume-builder/:id"
            element={
              <ProtectedRoute>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />

          {/* JobSeeker routes with sidebar navigation */}
          <Route
            path="/jobseeker-dashboard"
            element={
              <ProtectedRoute requiredRole="job_seeker">
                <JobSeekerDashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Default route */}
            <Route index element={<DashboardHome />} />

            {/* Individual section routes */}
            <Route path="jobs" element={<JobsSection />} />
            <Route path="saved" element={<SavedJobsSection />} />
            <Route path="applications" element={<ApplicationsSection />} />
            <Route path="resume" element={<ResumeSection />} />
            <Route path="alerts" element={<AlertsSection />} />
            <Route path="settings" element={<SettingsSection />} />
            <Route path="edit-profile" element={<EditProfileSection />} />
            <Route path="following" element={<FollowingSection />} />
          </Route>

          {/* Redirect legacy jobseeker path to canonical */}
          <Route
            path="/jobseeker/dashboard"
            element={<Navigate to="/jobseeker-dashboard" replace />}
          />

          {/* Employer routes */}
          <Route
            path="/employer/dashboard"
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer-dashboard"
            element={<Navigate to="/employer/dashboard" replace />}
          />

          {/* Admin routes removed */}
          {/* Super Admin routes */}
          <Route
            path="/super-admin/dashboard"
            element={
              <ProtectedRoute requiredRole="super_admin">
                <ErrorBoundary>
                  <SuperAdminDashboardPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />


          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isDashboardRoute && <FooterEnhanced />}
    </div>
  );
};

import { apiClient } from './lib/api-client';
import { normalizeTier } from './utils/tier';
import { normalizeMediaUrl } from './utils/media';

// Wrapper component to provide proper props to JobDetails using app/data contexts
const JobDetailsRoute: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { jobs, loading } = useData();
  const { currentUser, applications, savedJobs, handleApplyJob, handleSaveJob } = useApp();

  // Support both UUID IDs, backend slugs, and client-generated slugs
  const job = jobs.find((j: any) => j.id === id || j.slug === id || slugify(String(j.title)) === id);

  const [resolvedJob, setResolvedJob] = React.useState<any>(job);

  // Helper to transform raw DB job to frontend Job interface (similar to DataContext)
  const transformJob = (dbJob: any) => {
    const rawTier = dbJob.tier || dbJob.type || '';
    const tierCanon = normalizeTier(rawTier) || (dbJob.source === 'newspaper' ? 'newspaper' : (dbJob.employment_type === 'full_time' ? 'premium' : 'latest'));
    const typeFromTier = tierCanon === 'megajob' ? 'mega_job'
      : tierCanon === 'premium' ? 'premium_job'
        : tierCanon === 'prime' ? 'prime_job'
          : tierCanon === 'newspaper' ? 'newspaper_job'
            : 'latest_job';

    return {
      id: dbJob.id || dbJob._id,
      title: dbJob.title || 'Untitled Job',
      company: dbJob.company?.name || dbJob.company || dbJob.company_id || 'Unknown Company',
      companyId: String(dbJob.company_id || dbJob.companyId || '').trim() || undefined,
      location: dbJob.location || 'Not specified',
      description: dbJob.description || 'No description available',
      desiredCandidate: (dbJob as any).desired_candidate || (dbJob as any).desiredCandidate || '',
      requirements: Array.isArray(dbJob.requirements)
        ? dbJob.requirements
        : (typeof dbJob.requirements === 'string' ? [dbJob.requirements] : []),
      postedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      publishedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      deadline: dbJob.deadline
        ? new Date(dbJob.deadline).toISOString().split('T')[0]
        : dbJob.updated_at
          ? new Date(new Date(dbJob.updated_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      salary: (() => {
        const currency = String(dbJob.salary_currency || 'NPR');
        const type = String(dbJob.salary_type || '').trim().toLowerCase();
        const pay = String(dbJob.pay_type || '').trim();
        if (dbJob.salary_min && dbJob.salary_max) {
          return `${currency} ${Number(dbJob.salary_min).toLocaleString()} - ${Number(dbJob.salary_max).toLocaleString()}`;
        }
        const s = String(dbJob.salary || dbJob.salary_range || '').trim();
        if (s) return s;
        if (type === 'negotiable' || type.includes('negotiable')) {
          return pay ? `Negotiable (${pay})` : 'Negotiable';
        }
        return 'Negotiable';
      })(),
      type: dbJob.type || typeFromTier,
      tier: tierCanon,
      category: dbJob.category?.name || dbJob.category_id || 'General',
      experience: dbJob.experience_level === 'senior' ? 'Senior Level' : dbJob.experience_level === 'mid' ? 'Mid Level' : (dbJob.experience_level || 'Entry Level'),
      tags: Array.isArray(dbJob.tags) && dbJob.tags.length > 0 ? dbJob.tags : [],
      featured: !!dbJob.featured,
      urgent: !!dbJob.urgent,
      logo: dbJob.company?.logo_url || dbJob.logo || '',
      coverImageUrl: dbJob.cover_image_url || dbJob.coverImageUrl || dbJob.coverImage || null,
      source: dbJob.source || undefined,
    };
  };

  React.useEffect(() => {
    // 1. If we have a robust job object from context (has desiredCandidate), use it directly.
    const hasDetails = job && String(((job as any)?.desiredCandidate ?? (job as any)?.desired_candidate ?? '')).trim();
    if (hasDetails) {
      setResolvedJob(job);
      return;
    }

    // 2. If not, we need to fetch the single job to get details (like desiredCandidate).
    // We avoid calling refreshJobs() because that fetches the list again (which might still lack details)
    // and triggers a re-render loop.
    let isActive = true;
    (async () => {
      const effectiveId = job?.id || id;
      if (!effectiveId) return;

      try {
        // Try to fetch individual job details
        const res = await apiClient.getJob(effectiveId);
        // Important: getJob returns { job: { ... } } or just the job object
        const rawJob = (res as any)?.job || res;

        if (isActive && rawJob) {
          try {
            const transformed = transformJob(rawJob);
            setResolvedJob(transformed);
          } catch (transformErr) {
            console.warn('Failed to transform fetched job:', transformErr);
            setResolvedJob(job || rawJob);
          }
        }
      } catch (err) {
        // If fetch fails (e.g. 404), fall back to what we have or stay null
        console.warn('Failed to fetch individual job:', err);
        if (isActive) setResolvedJob(job);
      }
    })();

    return () => { isActive = false; };
  }, [id, job]);

  if (loading && !resolvedJob) {
    return <Loader fullScreen text="Loading job details..." />;
  }
  const jobId = resolvedJob?.id || job?.id || id;
  const relatedJobs = jobs.filter((j: any) => j.id !== jobId).slice(0, 6);
  const isSaved = jobId ? savedJobs.includes(jobId) : false;
  const hasApplied = jobId && currentUser ? applications.some(a => String(a.jobId) === String(jobId) && String(a.userId) === String(currentUser.id)) : false;

  return (
    <JobDetails
      job={resolvedJob as any}
      relatedJobs={relatedJobs as any}
      onApply={(jobId: string, applicationData?: any) => handleApplyJob(jobId, applicationData)}
      onSave={(jobId: string) => handleSaveJob(jobId)}
      isSaved={!!isSaved}
      hasApplied={!!hasApplied}
      onViewJob={(jobId: string) => {
        const target = jobs.find((j: any) => j.id === jobId);
        // Always prioritize the stored slug or the ID. Only slugify if absolutely necessary (which shouldn't be the case if backend handles slugs)
        const slug = (target as any)?.slug || jobId;
        navigate(`/jobs/${slug}`);
      }}
      onViewCompany={(companyName: string) => {
        navigate(`/company/${encodeURIComponent(companyName)}`);
      }}
      currentUser={currentUser}
    />
  );
};

// Wrapper route components for Forgot/Reset Password flows
const ForgotPasswordRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <ForgotPasswordForm onBackToLogin={() => navigate('/auth')} />
  );
};

const ResetPasswordOtpRoute: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || '';

  if (!email) {
    return <Navigate to="/auth/forgot-password" replace />;
  }

  return (
    <ResetPasswordWithOtpForm
      email={email}
      onSuccess={() => navigate('/auth')}
      onBackToLogin={() => navigate('/auth')}
    />
  );
};

const ResetPasswordRoute: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  return (
    <ResetPasswordForm
      resetToken={token}
      onSuccess={() => navigate('/auth')}
      onBackToLogin={() => navigate('/auth')}
    />
  );
};

export default AppRouter;
// Wrapper for AboutPage with team member slug
const AboutPageRoute: React.FC = () => {
  const { name } = useParams();
  return <AboutPage initialMemberSlug={String(name || '')} />;
};
